// apps/landing-page/src/components/search/SearchCombobox.tsx
// The navbar search input from `xl` up: a combobox with a popover under it. Not modal:
// no backdrop, no scroll lock, no focus trap.
'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { CatalogSearch } from './useCatalogSearch';
import { useSearchBox } from './useSearchBox';
import { SearchResults, hasSearchContent } from './SearchResults';

export function SearchCombobox({ search, className }: { search: CatalogSearch; className?: string }) {
  const t = useTranslations('search');
  const pathname = usePathname();
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Open only for the path it was opened on, so a route change closes it without an effect.
  const [openPath, setOpenPath] = useState<string | null>(null);
  const isOpen = openPath === pathname;
  const open = () => { setOpenPath(pathname); search.ensureLoaded(); };
  const close = () => setOpenPath(null);
  const done = () => { close(); inputRef.current?.blur(); };

  const box = useSearchBox(search, listId, done);
  const showPopover = isOpen && hasSearchContent(search, box);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // First Escape closes the popover and keeps focus in the input.
      if (isOpen) { e.preventDefault(); close(); }
      return;
    }
    if (!isOpen) {
      if (e.key === 'ArrowDown') { e.preventDefault(); open(); }
      else if (e.key === 'Enter') box.onKeyDown(e);
      return;
    }
    box.onKeyDown(e);
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative w-[280px] min-w-0', className)}
      onBlur={(e) => { if (!containerRef.current?.contains(e.relatedTarget as Node | null)) close(); }}
    >
      <Search aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        data-search-input
        aria-label={t('placeholder')}
        aria-keyshortcuts="Control+K Meta+K"
        aria-expanded={showPopover}
        aria-controls={showPopover ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showPopover ? box.activeDomId : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={t('placeholder')}
        value={box.query}
        onChange={(e) => { box.setQuery(e.target.value); open(); }}
        onFocus={open}
        onClick={open}
        onKeyDown={handleKeyDown}
        className="h-10 w-full min-w-0 rounded-full border border-slate-200 bg-slate-50 pl-10 pr-4 text-[16px] text-slate-900 text-ellipsis placeholder:text-slate-500 outline-none! transition-colors focus:border-teal-600 focus:bg-white focus:ring-2 focus:ring-teal-600/20"
      />

      {showPopover && (
        // mousedown is cancelled so a click on a result never blurs the input first
        // (Safari does not focus links on click, which would close the popover before the click lands).
        <div
          onMouseDown={(e) => e.preventDefault()}
          className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-[480px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl"
        >
          <SearchResults search={search} box={box} listId={listId} onSelect={done} />
        </div>
      )}
    </div>
  );
}
